import { useMemo } from 'react';
import { Sparkles, MapPin } from 'lucide-react';
import type { RouteItem } from '@/types/routeMap';
import { RouteStatList } from './RouteStatList';
import { YearSwitcher } from './YearSwitcher';
import { UnknownAirportsPanel } from './UnknownAirportsPanel';

interface RouteSidebarProps {
  routes: RouteItem[];
  unknownAirports: string[];
  year: number | 'all';
  yearOptions: number[];
  onYearChange: (year: number | 'all') => void;
  onEditAirport: (code: string) => void;
}

export function RouteSidebar({
  routes,
  unknownAirports,
  year,
  yearOptions,
  onYearChange,
  onEditAirport,
}: RouteSidebarProps) {
  const summary = useMemo(() => {
    const totalRoutes = routes.length;
    const totalCount = routes.reduce((s, r) => s + r.count, 0);
    const totalMinutes = routes.reduce((s, r) => s + (r.totalMinutes || 0), 0);
    const totalHours = Math.round(totalMinutes / 60);
    const airports = new Set<string>();
    for (const r of routes) {
      airports.add(r.departure);
      airports.add(r.arrival);
    }
    return { totalRoutes, totalCount, totalHours, airportCount: airports.size };
  }, [routes]);

  return (
    <aside className="hidden lg:flex flex-col w-[380px] bg-slate-950/60 backdrop-blur-xl ring-1 ring-white/10 border-l border-white/5 overflow-hidden">
      <div className="shrink-0 px-6 py-5 border-b border-white/5 space-y-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-300/90">
            <Sparkles className="w-4 h-4" />
            <h2 className="text-sm font-semibold tracking-wide">航线统计</h2>
          </div>
          <YearSwitcher value={year} options={yearOptions} onChange={onYearChange} />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <SummaryStat label="航线" value={summary.totalRoutes} />
          <SummaryStat label="航段" value={summary.totalCount} />
          <SummaryStat label="小时" value={summary.totalHours} />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <MapPin className="w-3 h-3" />
          途经 <span className="text-slate-300">{summary.airportCount}</span> 个机场
          {unknownAirports.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20">
              {unknownAirports.length} 待补录
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
        <RouteStatList routes={routes} topN={5} />
        <UnknownAirportsPanel codes={unknownAirports} onEdit={onEditAirport} />
      </div>
    </aside>
  );
}

interface SummaryStatProps {
  label: string;
  value: number;
}

function SummaryStat({ label, value }: SummaryStatProps) {
  return (
    <div className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2">
      <div className="text-lg font-semibold text-slate-100 tabular-nums">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

